import { localStorageKeys } from "@/_services/utils"
import axios from "axios"
import { getAuthorizationToken } from "./axios"
import { getHostConfig } from "./theming"
import { searchParamToObject } from "./utils"

function cleanup() {
  localStorage.removeItem(localStorageKeys.SSO_PARTNER.LOGOUT_URL)
}

export async function logout() {
  const logoutUrl = localStorage.getItem(localStorageKeys.SSO_PARTNER.LOGOUT_URL)

  // ssoId is also used by OIDC and SAML, so we need to add a second information to determine if this logout is necessary
  if (logoutUrl) {
    cleanup()
    window.location.href = logoutUrl
    return new Promise(resolve => {
      // the redirection is not instantaneous so we need a delay
      setTimeout(() => resolve(), 400)
    })
  }
}

export async function authenticate() {
  const { hash, search } = window.location

  const queryParams = searchParamToObject(search)
  const hostConfig = getHostConfig()
  let ssoPartnerId = queryParams.ssoPartner || hostConfig.ssoPartner

  /**
   * This is to handle authentification through iframe integration in web and mobile apps (Tagpay/Skaleet, etc.) or using transient tokens (Floa, SKB).
   * In addition to the token or jwt query params, this mode of integration if otfen used along the query param noDecoration.
   * The hash fallback parsing is for retro-compatibility, when we used to have the hash router instead of the history (browser) router.
   *
   * WARNING: This function must also work without a ssoPartner config.
   * For some partners, the backend identifies which config to use by looking up in the token payload directly.
   */
  const query = new URLSearchParams(search || hash?.substring(hash.indexOf("?")))
  const token = query.get("token") || query.get("jwt")
  if (token) {
    try {
      const response = await axios.post("/api/core/auth/token/login", { token })
      // Some hosts have multiple SSO partners configs, so we need to use the ssoId from the login response. Else, we run the risk
      // of using the wrong ssoPartner config if the user ssoId is not the same as the default ssoPartnerId from hosts.js
      ssoPartnerId = response.data?.user?.ssoId
    } catch {
      // This catch is empty on purpose. We need the rest of the authenticate() function to be executed anyway.
      // Whether the token login is successful or not, we need to continue because:
      // - If successful (logged in), we need to store the logoutUrl for later use in the logout function.
      // - If failed (not logged in):
      //    * If ssoPartner has a loginUrl, we must redirect the user to the SSO partner login page (loginUrl) for
      //      a new attempt. We cannot show the platform auth page to them, because they should use the partner page instead.
      //    * If a ssoPartner does not have a loginUrl, we let PagesLayout `loadUserConfiguration` function handle
      //      login check, and ultimately redirection to the platform auth page.
    }
  }

  // Make sure we're in a SSO partner context before continuing further
  // Continuing after this point is optional.
  // Indeed, if the provided partner or transient token is valid,
  // the axios interceptor would have already received a token from our platform at this point.
  // If we stop here, then the code execution resumes in App.js and then PagesLayout.jsx componentDidMount(),
  // which will attempt to load the user configuration.
  if (!ssoPartnerId) return

  const {
    data: { loginUrl, logoutUrl },
  } = await axios.get(`/api/core/sso-partner/${ssoPartnerId}`)

  // Store the logoutUrl for later use in the logout process.
  if (logoutUrl) localStorage.setItem(localStorageKeys.SSO_PARTNER.LOGOUT_URL, logoutUrl)

  const isLogged = getAuthorizationToken()
  if (!isLogged && loginUrl) {
    // The user is not logged in, it means either:
    // - He reached the platform via a direct link (eg: Bookmark), without using the ssoPartner login page,
    // - He reached the platform without token in the URL (or with an invalid/expired token).
    // In both cases, we need to redirect the user to the ssoPartner login page, to prevent the user
    // from being confused by the platform login page and not being able to find how to login.
    window.open(loginUrl, "_self")
    return { isRedirectingToSsoPartner: true }
  }
}
